// Lỗi "chế độ non-interactive, người gọi ĐÃ chọn abort" — khác hẳn "thiếu cờ".
//
// VÌ SAO CÓ FILE NÀY (#293): trước đây mọi nhánh `abort` đều mượn `MissingFlagError`,
// nên JSON ra stderr khai `missing_flag: "--on-clone-fail"` NGAY CẢ khi người gọi vừa
// truyền đúng cờ đó. Extension (#276) đọc field ấy sẽ thêm lại cờ đang có rồi hỏng
// vòng hai — không có đường thoát. Tệ hơn: nguyên nhân thật (lỗi git, gh chưa login)
// chỉ nằm ở stdout người-đọc, đúng thứ hợp đồng máy-đọc sinh ra để khỏi phải parse.
//
// Ranh giới giữa hai lớp lỗi — chỉ một câu hỏi: **cờ có mặt trong argv không?**
//   - cờ VẮNG   → `MissingFlagError`. "Thêm cờ vào" là cách sửa đúng.
//   - cờ CÓ MẶT, giá trị `abort` → lớp dưới đây. Thêm cờ KHÔNG sửa được gì; phải
//     chữa nguyên nhân thật (URL/mạng/credential) hoặc đổi sang `retry`/`skip`.
//
// Tách file riêng thay vì nhét vào `non-interactive-context.ts`: file đó đã 189 dòng
// và đang giữ một việc khác (phát hiện chế độ + đọc cờ).
import { JSON_SCHEMA_VERSION } from "./machine-readable-json-output-contract.js";
import {
  isMissingFlagError,
  type MissingFlagError,
  reportMissingFlag,
} from "./non-interactive-context.js";

/**
 * Bước recovery bị chặn vì người gọi đã chọn `abort` (tường minh hoặc mặc định của
 * một cờ ĐANG CÓ trong argv).
 *
 * Mang `reason` + `detail` để phía đọc phân biệt được "clone hỏng" với "thư mục đã
 * tồn tại" mà không phải đoán từ text.
 */
export class NonInteractiveAbortError extends Error {
  readonly command: string;
  /** Mã máy-đọc của tình huống: `clone_failed` | `existing_dir` | `step_failed`. */
  readonly reason: string;
  /** Cờ đã quyết định abort — để phía đọc biết đổi giá trị NÀO thì chạy tiếp được. */
  readonly abortedBy: string;
  /** Nguyên nhân thật (message của git/gh/...). Có thể vắng nếu tầng dưới không trả. */
  readonly detail?: string;
  readonly hint: string;

  constructor(params: {
    command: string;
    reason: string;
    abortedBy: string;
    detail?: string;
    hint: string;
  }) {
    super(
      `Dừng ở chế độ non-interactive: ${params.reason}` +
        (params.detail ? ` — ${params.detail}` : "") +
        ` (\`${params.abortedBy} abort\`)`,
    );
    this.name = "NonInteractiveAbortError";
    this.command = params.command;
    this.reason = params.reason;
    this.abortedBy = params.abortedBy;
    this.detail = params.detail;
    this.hint = params.hint;
  }
}

/** Nhận diện qua `name` — `instanceof` gãy khi module bị nạp 2 lần (ESM + bundle). */
export function isNonInteractiveAbortError(err: unknown): err is NonInteractiveAbortError {
  return err instanceof Error && err.name === "NonInteractiveAbortError";
}

/**
 * Lỗi này có phải loại "phải nổi thẳng lên `index.ts` để in JSON" không?
 *
 * Dùng thay `isMissingFlagError` ở MỌI guard rethrow: guard cũ chỉ biết một lớp, nên
 * lớp mới sẽ bị `catch` chung nuốt mất và ra stack trace không parse được.
 */
export function isNonInteractiveFatalError(
  err: unknown,
): err is MissingFlagError | NonInteractiveAbortError {
  return isMissingFlagError(err) || isNonInteractiveAbortError(err);
}

// Cắt userinfo khỏi mọi URL trong text trước khi đưa vào JSON máy-đọc.
//
// VÌ SAO BẮT BUỘC: `detail` lấy nguyên `stderr` của `git ls-remote`, mà git in lại
// NGUYÊN URL người dùng truyền — kể cả dạng `https://user:TOKEN@host/repo.git`.
// Luật 3 của hợp đồng máy-đọc: bí mật KHÔNG BAO GIỜ vào JSON, vì output này bị máy
// khác đọc và có thể bị log lại (extension #276 ghi panel log) ⇒ coi như public.
//
// Đặt ở đây — chỗ nghẽn DUY NHẤT trước khi ghi — thay vì ở từng nơi dựng `detail`:
// nơi dựng thì mỗi lần thêm một producer là một lần quên.
const URL_USERINFO = /([a-zA-Z][a-zA-Z0-9+.-]*:\/\/)[^/@\s]+@/g;

export function redactUrlCredentials(text: string): string {
  return text.replace(URL_USERINFO, "$1***@");
}

/**
 * In JSON 1 dòng ra stderr cho CẢ HAI lớp lỗi rồi trả mã thoát.
 *
 * ⛔ Payload abort KHÔNG mang field `missing_flag` — đó chính là lỗi #293. Cờ được
 * gợi ý nằm ở `hint`/`aborted_by`, đúng nghĩa của chúng.
 */
export function reportNonInteractiveError(
  err: MissingFlagError | NonInteractiveAbortError,
): number {
  if (!isNonInteractiveAbortError(err)) return reportMissingFlag(err);
  const payload: Record<string, unknown> = {
    schema_version: JSON_SCHEMA_VERSION,
    command: err.command,
    reason: err.reason,
    aborted_by: err.abortedBy,
    hint: err.hint,
  };
  if (err.detail) payload.detail = redactUrlCredentials(err.detail);
  process.stderr.write(`${JSON.stringify(payload)}\n`);
  return 1;
}
